import axios, { CancelTokenSource } from "axios";
import { protectedResources } from "../context/AuthContext/authConfig";
import { UserWithTopics } from "../context/ConfigContext/types";
import {
	AdminPanelClient,
	AdminPanelTopic,
	Scraper,
} from "../components/TopicsPanel/types";
import { Document, GetItemsRequestBody } from "../types";
import {
	EmbedParams,
	Endpoint,
	FetchResponse,
	RequestType,
	WizardInput,
	WizardOutput,
} from "./types";
import { apiRequest } from "./helpers";
import { UserWithClients } from "../context/ClientAccessContext/types";

export const getUserWithClientAccesses = async () => {
	return axios
		.get(protectedResources.scraperAPI.endpoint + Endpoint.USER_CLIENT_ACCESS)
		.then((response) => {
			if (response.data.result.clients?.length === 0)
				throw new Error("User does not have any client access");
			return response.data;
		})
		.catch((error) => console.log(error)) as Promise<
		FetchResponse<UserWithClients>
	>;
};

let currentDocumentRequestToken: CancelTokenSource | null = null;
export const getDocuments = async (reqBody: GetItemsRequestBody) => {
	if (currentDocumentRequestToken) {
		currentDocumentRequestToken.cancel(
			"A new request was made before the previous completed."
		);
	}

	currentDocumentRequestToken = axios.CancelToken.source();
	const url = protectedResources.scraperAPI.endpoint + Endpoint.DOCUMENTS;

	try {
		const response = await axios.post(url, reqBody, {
			cancelToken: currentDocumentRequestToken.token,
		});
		currentDocumentRequestToken = null;
		return response.data;
	} catch (error) {
		if (axios.isCancel(error)) {
			console.log("Previous request was canceled");
		} else {
			console.error(error);
		}
	}
};
// export const getDocuments = async (reqBody: GetItemsRequestBody) => {
//   const url = protectedResources.scraperAPI.endpoint + Endpoint.DOCUMENTS;
//   return apiRequest(RequestType.POST, url, reqBody) as Promise<FetchResponse<Document[]>>;
// };

export const getUserWithTopicAccesses = async () => {
	const url =
		protectedResources.scraperAPI.endpoint + Endpoint.USER_TOPIC_ACCESS;
	const response = await apiRequest(RequestType.GET, url);
	if (response && response.result.topics?.length === 0) {
		throw new Error("User does not have any topic access");
	}
	return response as Promise<FetchResponse<UserWithTopics>>;
};

export const getAllUsersWithTopics = async () => {
	const url =
		protectedResources.scraperAPI.endpoint + Endpoint.All_USERS_TOPICS;
	return apiRequest(RequestType.GET, url, null) as Promise<
		FetchResponse<UserWithTopics[]>
	>;
};

export const getAllClients = async () => {
	const url = protectedResources.scraperAPI.endpoint + Endpoint.CLIENTS;
	return apiRequest(RequestType.GET, url, null) as Promise<
		FetchResponse<AdminPanelClient[]>
	>;
};

export const getAllUsersWithClients = async () => {
	const url =
		protectedResources.scraperAPI.endpoint + Endpoint.All_USERS_CLIENTS;
	return apiRequest(RequestType.GET, url, null) as Promise<
		FetchResponse<UserWithClients[]>
	>;
};

export const getAllLabelsForCurrentTopic = async (topicId: number) => {
	const url =
		protectedResources.scraperAPI.endpoint + `${Endpoint.LABELS}/${topicId}`;
	return apiRequest(RequestType.GET, url) as Promise<FetchResponse<any[]>>;
};

export const getAllTopics = async () => {
	const url = protectedResources.scraperAPI.endpoint + Endpoint.TOPICS;
	return apiRequest(RequestType.GET, url) as Promise<
		FetchResponse<AdminPanelTopic[]>
	>;
};

export const getAllScrapers = async () => {
	const url = protectedResources.scraperAPI.endpoint + Endpoint.SCRAPERS;
	return apiRequest(RequestType.GET, url) as Promise<FetchResponse<Scraper[]>>;
};

export const getPowerBIContent = async (clientId) => {
	const url =
		protectedResources.scraperAPI.endpoint +
		Endpoint.EMBED_REPORT +
		"/" +
		clientId;
	return apiRequest(RequestType.GET, url, null) as Promise<
		FetchResponse<EmbedParams>
	>;
};

export const getFullDocumentText = async (
	documentId: number,
	clientName: string
) => {
	const url = `${protectedResources.scraperAPI.endpoint}${Endpoint.DOCUMENTS}/${documentId}/fulltext/${clientName}`;
	return apiRequest(RequestType.GET, url, null) as Promise<
		FetchResponse<string>
	>;
};

export const getSummaryDocumentText = async (
	documentId: number,
	clientName: string
) => {
	const url = `${protectedResources.scraperAPI.endpoint}${Endpoint.DOCUMENTS}/${documentId}/summary/${clientName}`;
	return apiRequest(RequestType.GET, url, null) as Promise<
		FetchResponse<string>
	>;
};

export const translateText = async (
	documentId: number,
	clientName: string,
	text: string,
	targetLanguage = "English"
) => {
	const url = `${protectedResources.scraperAPI.endpoint}${Endpoint.DOCUMENTS}/${documentId}/translate/${clientName}?targetLanguage=${targetLanguage}`;
	return apiRequest(RequestType.POST, url, { text }) as Promise<
		FetchResponse<string>
	>;
};

export const generateSearchTerms = async (
	clientName: string,
	topicName: string,
	userMessage: string
) => {
	const requestType = "generate_search_terms";
	const body: WizardInput = {
		clientName,
		topicName,
		requestType,
		promptData: {
			userMessage: userMessage,
		},
	};
	const url = `${protectedResources.scraperAPI.endpoint}${Endpoint.WIZARD}`;
	return apiRequest(RequestType.POST, url, body) as Promise<
		FetchResponse<WizardOutput>
	>;
};

export const getDocumentById = async (documentId: number) => {
	const url = `${protectedResources.scraperAPI.endpoint}${Endpoint.DOCUMENTS}/${documentId}`;
	return apiRequest(RequestType.GET, url, null) as Promise<
		FetchResponse<Document>
	>;
};
